import { useEffect, useState } from 'react';
import { api, ApiError } from '../api/client';
import { Badge, ErrorBox } from '../components/common';
import { formatDate } from '../labels';
import '../styles/pilotage.css';

interface State {
  environnement: string;
  moteurReglesVersion: string;
  schema: {
    attendues: number;
    appliquees: number;
    aJour: boolean;
    derniere: { version: string; applied_at: string } | null;
  };
  interfaces: { code: string; label: string; question: string; mode: 'simulation' }[];
  rapprochement: { date: string; anomalies: number } | null;
  synchronisation7j: Record<string, number>;
}

/** Pictogrammes au trait (maquette S24), décoratifs. */
const PATHS = {
  server: 'M3 4h18v6H3zM3 14h18v6H3zM7 7h.01M7 17h.01',
  grid: 'M4 4h16v16H4zM4 9h16M9 9v11',
  list: 'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01',
  send: 'M22 2 11 13M22 2l-7 20-4-9-9-4z',
  warn: 'M12 3 2 21h20zM12 10v4M12 18h.01',
} as const;

function LineIcon({ name, width = 1.8 }: { name: keyof typeof PATHS; width?: number }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={width}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={PATHS[name]} />
    </svg>
  );
}

/** Espace Exploitation (dossier §4) : supervision technique, sans aucun droit métier ni donnée nominative. */
export function ExploitationPage() {
  const [s, setS] = useState<State | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  useEffect(() => {
    api<State>('/exploitation/etat').then(setS).catch(setError);
  }, []);
  if (!s) return error ? <ErrorBox error={error} /> : <p>Chargement…</p>;
  return (
    <div className="stack">
      <div className="head">
        <div>
          <h1>Exploitation</h1>
          <p className="lead">
            Supervision technique. Cet espace ne donne accès à aucune personne, demande ni inscription ; les
            procédures (sauvegarde, restauration, tâches périodiques) sont décrites dans le guide
            d’exploitation.
          </p>
        </div>
      </div>
      <div className="kpis">
        <div className="stat">
          <span className="ic">
            <LineIcon name="server" />
          </span>
          <span className="v pl-v-text">{s.schema.aJour ? 'À jour' : 'En retard'}</span>
          <span className="l">
            Schéma : {s.schema.appliquees}/{s.schema.attendues} migrations
            {s.schema.derniere && (
              <>
                {' '}
                - dernière <span className="mono pl-ref">{s.schema.derniere.version}</span>
              </>
            )}
          </span>
        </div>
        <div className="stat">
          <span className="ic">
            <LineIcon name="grid" />
          </span>
          <span className="v pl-v-mono">{s.environnement}</span>
          <span className="l">Environnement - moteur de règles {s.moteurReglesVersion}</span>
        </div>
        <div className="stat">
          <span className="ic">
            <LineIcon name="list" />
          </span>
          <span className="v">{s.rapprochement ? s.rapprochement.anomalies : '-'}</span>
          <span className="l">
            {s.rapprochement
              ? `Anomalies au dernier rapprochement (${formatDate(s.rapprochement.date)})`
              : 'Aucun rapprochement exécuté'}
          </span>
        </div>
        <div className="stat">
          <span className="ic">
            <LineIcon name="send" />
          </span>
          <span className="v">{s.synchronisation7j.conflit ?? 0}</span>
          <span className="l">
            Conflits de synchronisation sur 7 jours ({s.synchronisation7j.appliquee ?? 0} commande(s)
            appliquée(s))
          </span>
        </div>
      </div>
      <section className="card np">
        <div className="pl-card-h">
          <h2 id="ex-interfaces-h">Interfaces externes</h2>
        </div>
        <div className="table-wrap">
          <table aria-labelledby="ex-interfaces-h">
            <thead>
              <tr>
                <th scope="col">Interface</th>
                <th scope="col">Mode</th>
                <th scope="col">Contrat attendu</th>
              </tr>
            </thead>
            <tbody>
              {s.interfaces.map((i) => (
                <tr key={i.code}>
                  <td>{i.label}</td>
                  <td>
                    <Badge tone="muted">Contrat en attente</Badge>
                  </td>
                  <td>Question {i.question}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
